import {
  type RoutineInstanceEntry,
  routineHistory,
  routinesListForChild,
  routinesListForGuardian,
} from '@chorepop/core/client';
import { type Component, For, Match, Switch, createResource } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  viewerRole: 'guardian' | 'child';
  onBack: () => void;
};

export type StreakDay = {
  date: string;
  outcome: 'completed' | 'missed' | 'today';
  routineNames: string[];
};

function localDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function aggregateStreakDays(histories: RoutineInstanceEntry[][]): StreakDay[] {
  const grouped = new Map<string, RoutineInstanceEntry[]>();
  for (const instance of histories.flat()) {
    grouped.set(instance.scheduledDate, [...(grouped.get(instance.scheduledDate) ?? []), instance]);
  }
  const today = localDateString();
  return [...grouped.entries()]
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([date, entries]) => ({
      date,
      outcome: entries.some((entry) => entry.status === 'approved')
        ? 'completed'
        : date === today && entries.some((entry) => entry.status.startsWith('pending_'))
          ? 'today'
          : 'missed',
      routineNames: [...new Set(entries.map((entry) => entry.taskName))],
    }));
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00`));
}

export const StreakHistoryScreen: Component<Props> = (props) => {
  const [days] = createResource(async () => {
    const routines =
      props.viewerRole === 'child'
        ? await routinesListForChild(props.sessionId)
        : await routinesListForGuardian(props.sessionId);
    if (!routines.ok) throw new Error(routines.error.message);
    const histories = await Promise.all(
      routines.value
        .filter((routine) => routine.childAccountId === props.childAccountId)
        .map(async (routine) => {
          const history = await routineHistory(props.sessionId, routine.recurringTaskId, 365);
          return history.ok ? history.value : [];
        }),
    );
    return aggregateStreakDays(histories);
  });
  const completedCount = () => (days() ?? []).filter((day) => day.outcome === 'completed').length;

  return (
    <ActionScreen
      eyebrow="Routine streak"
      title={
        props.viewerRole === 'child'
          ? 'Your streak days'
          : `${props.childDisplayName}'s streak days`
      }
      onBack={props.onBack}
      class="action-screen--streak-history"
    >
      <section class="streak-history-summary">
        <strong>{completedCount()}</strong>
        <span>days earned</span>
        <p>A date counts once when at least one routine from that day is approved.</p>
      </section>
      <Switch>
        <Match when={days.loading}>
          <p class="child-dashboard-muted">Loading streak days…</p>
        </Match>
        <Match when={days.error}>
          <p class="form-error" role="alert">
            Couldn't load streak history: {(days.error as Error).message}
          </p>
        </Match>
        <Match when={(days()?.length ?? 0) === 0}>
          <section class="routine-empty">
            <strong>No streak days yet</strong>
            <p>Days with an approved routine will appear here.</p>
          </section>
        </Match>
        <Match when={(days()?.length ?? 0) > 0}>
          <ol class="streak-history-list" aria-label={`${props.childDisplayName}'s streak days`}>
            <For each={days()}>
              {(day) => (
                <li class={`streak-history-day is-${day.outcome}`}>
                  <span class="streak-history-marker" aria-hidden="true">
                    {day.outcome === 'completed' ? '★' : day.outcome === 'today' ? '•' : '×'}
                  </span>
                  <div>
                    <strong>{formatDate(day.date)}</strong>
                    <span>{day.routineNames.join(' · ')}</span>
                  </div>
                  <span class="history-chip">
                    {day.outcome === 'completed'
                      ? 'Counted'
                      : day.outcome === 'today'
                        ? 'Waiting today'
                        : 'Did not count'}
                  </span>
                </li>
              )}
            </For>
          </ol>
        </Match>
      </Switch>
    </ActionScreen>
  );
};
